
import { retainContext, FsmBuilder, SubFsmBuilder, Fsm, NoContext } from "little-fsm";
import HotelSearchFormState from "./HotelSearchFormState";
import { BookingDetail, BookingSession, HotelResult, HotelResultSet, Location, TemporaryRoomTypeSelection } from "../Models";
import HotelSearchCriteria from "./HotelSearchCriteria";
import { GuestFormContext, GuestFormSubManifest } from "./common/GuestFormSubManifest";
import RoomSearchCriteria from "./RoomSearchCriteria";
import BookingSearchFormState from "./BookingSearchFormState";
import BookingSearchCriteria from "./BookingSearchCriteria";

export type FetchingStatus = "fetching" | "success" | "error" 
export type SubmissionStatus = "submitting" | "success" | "error"
export type WaitingStatus = "waiting" | "timeout"

export class HotelContext {
    constructor(
        public readonly hotelSearchFormState: HotelSearchFormState,
        public readonly searchCriteria: HotelSearchCriteria,
        public readonly hotelResultSet: HotelResultSet|null,
        public readonly hotelResult: HotelResult|null
    ){}

    withFormState(hotelSearchFormState: HotelSearchFormState){
        return new HotelContext(hotelSearchFormState, this.searchCriteria, this.hotelResultSet, this.hotelResult);
    }

    withFormStateAndSearchCriteria(hotelSearchFormState: HotelSearchFormState, searchCriteria: HotelSearchCriteria){
        return new HotelContext(hotelSearchFormState, searchCriteria, this.hotelResultSet, this.hotelResult);
    }

    withHotelResult(hotelResult: HotelResult){
        return new HotelContext(this.hotelSearchFormState, this.searchCriteria, this.hotelResultSet, hotelResult);
    }

    withHotelResultSet(hotelResultSet: HotelResultSet){
        return new HotelContext(this.hotelSearchFormState, this.searchCriteria, hotelResultSet, this.hotelResult);
    }

    withSearchCriteriaAndHotelResultSet(searchCriteria: HotelSearchCriteria, hotelResultSet: HotelResultSet){
        return new HotelContext(this.hotelSearchFormState, searchCriteria, hotelResultSet, this.hotelResult);
    }
}

export type HotelSubMainifest = {
    context: HotelContext,
    substates: [
        'search_progressing', 
        'showing_result',
        'selecting_hotel'
    ],
    events: {
        'search_succeeded': {
            searchCriteria: HotelSearchCriteria,
            hotelResultSet: HotelResultSet
        },
        'search_failed': {},
        'search_submitted': {
            searchCriteria: HotelSearchCriteria
        },
        'hotel_requested': {
            hotelResult: HotelResult
        },
        'hotel_selected': {},
        'back': {}
    }
}

export class HotelAndRoomSelectionContext{
    constructor(
        public readonly hotelContext: HotelContext|undefined,
        public readonly searchCriteria: RoomSearchCriteria|undefined,
        public readonly roomTypeSelections:TemporaryRoomTypeSelection[] = [],
        public readonly bookingSession: BookingSession|null = null,
    ){}

    withSearchCriteria(searchCriteria: RoomSearchCriteria){
        return new HotelAndRoomSelectionContext(this.hotelContext, searchCriteria, this.roomTypeSelections, this.bookingSession)
    }

    withRoomTypeSelections(roomTypeSelections: TemporaryRoomTypeSelection[]){
        return new HotelAndRoomSelectionContext(this.hotelContext, this.searchCriteria, roomTypeSelections, this.bookingSession)
    }

    withBookingSession(bookingSession: BookingSession){
        return new HotelAndRoomSelectionContext(this.hotelContext, this.searchCriteria, this.roomTypeSelections, bookingSession)
    }

    withCriteriaAndBookingSession(searchCriteria: RoomSearchCriteria, bookingSession: BookingSession){
        return new HotelAndRoomSelectionContext(this.hotelContext, searchCriteria, this.roomTypeSelections, bookingSession);
    }
}

export type HotelAndRoomSelectionSubManifest = {
    context: HotelAndRoomSelectionContext
    substates: [
        'initial',
        'search_progressing',
        'showing_result',
        'selection_empty', 
        'selection_partial', 
        'selection_complete',
        'confirming_exit_selection_partial',
        'confirming_exit_selection_complete',
        'checking_out'                
    ],
    events: {
        'booking_session_created': {
            searchCriteria: RoomSearchCriteria,
            bookingSession: BookingSession
        }
        'search_submitted': {
            searchCriteria: RoomSearchCriteria
        }
        'search_succeeded': {
            bookingSession: BookingSession
        },
        'room_selection_requested': {}
        'room_selection_emptied': {
            roomTypeSelections: TemporaryRoomTypeSelection[]
        },
        'room_selection_incompleted': {
            roomTypeSelections: TemporaryRoomTypeSelection[]
        },
        'room_selection_completed': {
            roomTypeSelections: TemporaryRoomTypeSelection[]
        },
        'checked_out_requested': {
            roomTypeSelections: TemporaryRoomTypeSelection[]
        },
        'checked_out_succeed': {
            bookingSession: BookingSession
        },
        'checked_out_failed': {},
        'back': {},
        'exit_confirmed': {
            roomTypeSelections: TemporaryRoomTypeSelection[]
        },
        'exit_cancelled': {}
    }
}

export class FindBookingContext{
    constructor(
        public readonly bookingSearchFormState: BookingSearchFormState,
        public readonly bookingDetails: BookingDetail|null
    ){}

    withBookingSearchFormState(bookingSearchFormState: BookingSearchFormState){
        return new FindBookingContext(bookingSearchFormState, this.bookingDetails);
    }

    withBookingDetails(bookingDetails: BookingDetail){
        return new FindBookingContext(this.bookingSearchFormState, bookingDetails);
    }
}

export type FindBookingSubManifest = {
    context: FindBookingContext
    substates: [
        'initial',
        'selecting_location',
        'selecting_date_range',
        'searching_booking',
        'showing_otp_form',
        'resending_otp',
        'checking_otp',
        'booking_details'                
    ],
    events: {
        'location_requested': {},
        'location_selected': {
            selectedLocation: Location
        },
        'date_range_requested': {},
        'date_range_selected': {
            checkInDate: string,
            checkOutDate: string;
        },
        'search_booking_successed': {},
        'search_booking_failed': {},
        'search_booking_requested': {
            searchCriteria: BookingSearchCriteria
        },
        'otp_submitted': {},
        'otp_corrected': {},
        'otp_wrong': {},
        'otp_resend_requested': {},
        'booking_form_requested': {},
        'booking_details_requested': {
            booking: BookingDetail
        },
        'back': {}
    }
}

export type DesktopManifiest = {
    states: {
        'home': {
            context: {
                hotelSearchFormState: HotelSearchFormState
            }
        },
        'hotels': HotelSubMainifest,
        'room_selection': HotelAndRoomSelectionSubManifest,
        'guest_form': GuestFormSubManifest,
        'find_booking': FindBookingSubManifest
    },
    events: {
        'search_submitted': {
            searchCriteria: HotelSearchCriteria
        },
        'back' : {}
    }
}

function buildHotelsSubFsmBuilder(): SubFsmBuilder<DesktopManifiest['states']['hotels']> {
    let subBuilder = new SubFsmBuilder<DesktopManifiest['states']['hotels']>();
    subBuilder.entrySubstate('search_progressing')
        .entrySubstate("showing_result", {
            description:"results_exits", 
            satisfies:(c) => {
                //console.log(c.hotelResultSet)
                return c.hotelResultSet != null;
            }
        });
    
    subBuilder.substate('search_progressing')
        .transition('search_succeeded', 'showing_result', (ctx, event) => {
            return ctx.withSearchCriteriaAndHotelResultSet(event.searchCriteria, event.hotelResultSet)
        })

    subBuilder.substate('showing_result')
        .transition('search_submitted', 'search_progressing', (ctx, event) => {
            let hotelSearchFormState = new HotelSearchFormState();
            hotelSearchFormState.checkInDate = event.searchCriteria.getCheckInDate();
            hotelSearchFormState.checkOutDate = event.searchCriteria.getCheckOutDate();
            hotelSearchFormState.location = event.searchCriteria.getLocation();
            hotelSearchFormState.numberOfRooms = event.searchCriteria.getNumberOfRooms();
            hotelSearchFormState.guestType = event.searchCriteria.getGuestType();

            if(event.searchCriteria.getFilterByTownship() != null){
                hotelSearchFormState.toHotelSearchCriteria().withFilterByPriceRange(event.searchCriteria.getFilterByTownship()!!);
            }

            if(event.searchCriteria.getFilterByPriceRange() != null){
                hotelSearchFormState.toHotelSearchCriteria().withFilterByPriceRange(event.searchCriteria.getFilterByPriceRange()!!);
            }

            if(event.searchCriteria.getSortByPrice() != null){
                hotelSearchFormState.toHotelSearchCriteria().withSortByPrice(event.searchCriteria.getSortByPrice()!!);
            }
        
            return ctx.withFormStateAndSearchCriteria(hotelSearchFormState, event.searchCriteria);
        })
        .transition('hotel_requested', 'selecting_hotel', (ctx, event) => {
            return ctx.withHotelResult(event.hotelResult);
        })
        .termination('back', retainContext)

    subBuilder.substate('selecting_hotel')
        .transition('hotel_selected', 'showing_result', retainContext)

    return subBuilder;
}

function buildRoomSelectionSubFsmBuilder(): SubFsmBuilder<DesktopManifiest['states']['room_selection']>  {
    let subBuilder = new SubFsmBuilder<DesktopManifiest['states']['room_selection']>();     
    subBuilder
        .entrySubstate('initial');

    subBuilder.substate('initial')
        .transition('booking_session_created', 'showing_result', (ctx, event) => {
            return ctx.withCriteriaAndBookingSession(event.searchCriteria, event.bookingSession);
        })
        .transition('search_submitted', 'search_progressing', (ctx, event) => {
            return ctx.withSearchCriteria(event.searchCriteria);
        })

    subBuilder.substate('search_progressing')
        .transition('search_succeeded', 'showing_result', (ctx, event) => {
            return ctx.withBookingSession(event.bookingSession);
        })
        
    
    subBuilder.substate('showing_result')
        .transition('room_selection_requested', 'selection_empty', retainContext)

    subBuilder.substate('selection_empty')
        .transition('room_selection_incompleted', 'selection_partial', (ctx, event) => {
            return ctx.withRoomTypeSelections(event.roomTypeSelections)
        })
        .transition('room_selection_completed', 'selection_complete', (ctx, event) => {
            return ctx.withRoomTypeSelections(event.roomTypeSelections)
        })
        .transition('search_submitted', 'search_progressing', (ctx, event) => {
            return ctx.withSearchCriteria(event.searchCriteria);
        })
        .termination('back', retainContext);

    subBuilder.substate('selection_partial')
        .transition('room_selection_emptied', 'selection_empty', (ctx, event) => {
            return ctx.withRoomTypeSelections(event.roomTypeSelections)
        })
        .transition('room_selection_incompleted', 'selection_partial', (ctx, event) => {
            return ctx.withRoomTypeSelections(event.roomTypeSelections)
        })
        .transition('room_selection_completed', 'selection_complete', (ctx, event) => {
            return ctx.withRoomTypeSelections(event.roomTypeSelections)
        })
        .transition('back', 'confirming_exit_selection_partial', retainContext)

    subBuilder.substate('selection_complete')        
        .transition('room_selection_emptied', 'selection_empty', (ctx, event) => {
            return ctx.withRoomTypeSelections(event.roomTypeSelections)
        })
        .transition('room_selection_incompleted', 'selection_partial', (ctx, event) => {
            return ctx.withRoomTypeSelections(event.roomTypeSelections)
        })
        .transition('back', 'confirming_exit_selection_complete', retainContext)
        .transition('checked_out_requested', 'checking_out', (ctx, event) => {
            console.log(ctx)
            return ctx.withRoomTypeSelections(event.roomTypeSelections);
        })

    subBuilder.substate('checking_out')
        .completion('checked_out_succeed', (ctx, event) => {
            return ctx.withBookingSession(event.bookingSession)
        })
        .termination('checked_out_failed', retainContext)

    subBuilder.substate('confirming_exit_selection_partial')
        .transition('back', 'selection_partial', retainContext)
        .transition('exit_cancelled', 'selection_partial', retainContext)
        .termination('exit_confirmed', retainContext);

    subBuilder.substate('confirming_exit_selection_complete')
        .transition('back', 'selection_complete', retainContext)
        .transition('exit_cancelled', 'selection_complete', retainContext)
        .termination('exit_confirmed', retainContext);

    return subBuilder;
}

function buildGuestFormSubFsmBuilder(): SubFsmBuilder<DesktopManifiest['states']['guest_form']> {
    let subBuilder = new SubFsmBuilder<DesktopManifiest['states']['guest_form']>();
    subBuilder.entrySubstate('showing_guest_form')

    subBuilder.substate('showing_guest_form')
        .transition('guest_info_submitted', 'creating_tentative_booking', (ctx, event) => {
            return ctx.withGuestInfoForm(event.guestInfoForm);
        })
        .transition('back', 'confirming_exit_guest_info', retainContext)

    subBuilder.substate('creating_tentative_booking')
        .completion('tentative_booking_created', (ctx, event) => {
            return ctx.withTentativeBookingResult(event.tentativeBookingResult);
        })
        .termination('tentative_booking_failed', retainContext)

    subBuilder.substate('confirming_exit_guest_info')
        .transition('exit_cancelled', 'showing_guest_form', retainContext)
        .termination('exit_confirmed', retainContext)

    return subBuilder;
}

function buildFindBookingSubFsmBuilder(): SubFsmBuilder<DesktopManifiest['states']['find_booking']>  {
    let subBuilder = new SubFsmBuilder<DesktopManifiest['states']['find_booking']>();     
    subBuilder.entrySubstate('initial')

    subBuilder.substate('initial')
        .transition('location_requested', 'selecting_location', retainContext)
        .transition('date_range_requested', 'selecting_date_range', retainContext)
        .transition('back', 'initial', retainContext)
        .transition('search_booking_requested', 'searching_booking', retainContext)
        .transition('booking_details_requested', 'booking_details',  (ctx, event) => {
            return ctx.withBookingDetails(event.booking);
        })

    subBuilder.substate('booking_details')
        .transition('back', 'initial', retainContext)

    subBuilder.substate('searching_booking')
        .transition('search_booking_successed', 'showing_otp_form', retainContext)
        .transition('search_booking_failed', 'initial', retainContext)

    subBuilder.substate('selecting_location')
        .transition('location_selected', 'initial', (ctx, event) => {
            let bookingSearchFormState = ctx.bookingSearchFormState;
            bookingSearchFormState.location = event.selectedLocation;
            return ctx.withBookingSearchFormState(bookingSearchFormState);
        })
        .transition('back', 'initial', retainContext)

    subBuilder.substate('selecting_date_range')
        .transition('date_range_selected', 'initial', (ctx, event) => {
            let bookingSearchFormState = ctx.bookingSearchFormState;
            bookingSearchFormState.checkInDate = event.checkInDate;
            bookingSearchFormState.checkOutDate = event.checkOutDate;
            return ctx.withBookingSearchFormState(bookingSearchFormState);
        })
        .transition('back', 'initial', retainContext)

    subBuilder.substate('showing_otp_form')
        .transition('otp_submitted', 'checking_otp', retainContext)
        .transition('otp_resend_requested', 'resending_otp', retainContext)
        .transition('back', 'initial', retainContext)

    subBuilder.substate('resending_otp')
        .transition('search_booking_successed', 'showing_otp_form', retainContext)
        .transition('search_booking_failed', 'initial', retainContext)

    subBuilder.substate('checking_otp')
        .transition('otp_corrected', 'initial', retainContext)
        .transition('otp_wrong', 'showing_otp_form', retainContext)

    return subBuilder;
}

export function appRootDesktopFsmBuilder(): Fsm<DesktopManifiest> {
    let builder = new FsmBuilder<DesktopManifiest>();

    builder.simpleState('home')
        .transition('search_submitted', 'hotels', (ctx, event) => {
            ctx.hotelSearchFormState.checkInDate = event.searchCriteria.getCheckInDate();
            ctx.hotelSearchFormState.checkOutDate = event.searchCriteria.getCheckOutDate();
            ctx.hotelSearchFormState.location = event.searchCriteria.getLocation();
            ctx.hotelSearchFormState.numberOfRooms = event.searchCriteria.getNumberOfRooms();
            ctx.hotelSearchFormState.guestType = event.searchCriteria.getGuestType();
            return new HotelContext(ctx.hotelSearchFormState, event.searchCriteria, null, null)
        })
        .transition('back', 'home', ctx => ctx)

    builder.compositeState('hotels', buildHotelsSubFsmBuilder())
        .transitionOnCompletion('room_selection', ctx => new HotelAndRoomSelectionContext(ctx, undefined))
        .transitionOnTermination('home', ctx => {
            return {
                hotelSearchFormState: ctx.hotelSearchFormState
            }
        })

    builder.compositeState('room_selection', buildRoomSelectionSubFsmBuilder())
        .transitionOnCompletion('guest_form', ctx => {
            return new GuestFormContext(ctx.bookingSession!!)
        })
        .transitionOnTermination('hotels', ctx => {
            return ctx.hotelContext!;
        })

    builder.compositeState('guest_form', buildGuestFormSubFsmBuilder())
        .transitionOnCompletion('guest_form', ctx => ctx)
        .transitionOnTermination('home', ctx => {
            return {
                hotelSearchFormState: new HotelSearchFormState()
            }
        })

    builder.compositeState('find_booking', buildFindBookingSubFsmBuilder())
        .transitionOnCompletion('home', ctx => {
            return {
                hotelSearchFormState: new HotelSearchFormState()
            }
        })

    return builder.build();
}